/**
 * SystemAuditLogPage
 *
 * Feature: 123-audit-log-viewer
 * Description: System audit log viewer with filters, search, quick filters, and export
 * Constitution: v1.12.0
 * Tasks: T017 (page), T022 (filters), T025 (search), T027 (quick filters), T031 (export)
 *
 * Conformed to Spec 197 design blueprint — migrated off MUI (Box/Paper/Table/Chip/
 * Drawer/Dialog) and the page-local MUI grid/filter/detail components onto the
 * canonical Tailwind+token UI kit. Presentation-only: data fetching, filter logic,
 * server pagination, export, and quick filters are unchanged. (The shared
 * components/AuditDataGrid|AuditFilters|QuickFilters|AuditDetailPanel are still
 * consumed by AuthAuditLogPage, so they are left untouched; the kit equivalents
 * are inlined here.)
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Download,
  RefreshCw,
  Filter,
  X as XIcon,
  Eye,
  ListFilter,
} from 'lucide-react';
import { useSystemAuditLog, useEntityTypes, useActions } from '../../hooks/useAuditQuery';
import { humanizeAuditLabel, actionBadgeVariant } from './components/auditLabels';
import { useAuth } from '../../hooks/useAuth';
import { exportSystemAuditCsv } from '../../services/auditViewerApi';
import type {
  SystemAuditLogEntry,
  SystemAuditLogFilters,
  QuickFilter,
  AuditAction,
} from '../../types/audit';
import ChangesDiff from './components/ChangesDiff';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Badge,
  EmptyState,
  Modal,
  Alert,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import { formatEastern } from '@/utils/dateFormatter';

// ============================================================================
// Helpers
// ============================================================================

function getDefaultDateRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

function toDateInputValue(isoString: string): string {
  return isoString.substring(0, 16); // YYYY-MM-DDTHH:mm
}

// Handles ANY action the backend records (CRUD + backup/restore/plan/schedule/storage/settings +
// future) — humanised label + intent-based colour, never a raw snake_case value.
function ActionBadge({ action }: { action: AuditAction }) {
  return (
    <Badge variant={actionBadgeVariant(action)} isOutlined>
      {humanizeAuditLabel(action)}
    </Badge>
  );
}

function getTodayRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return { startDate: start.toISOString(), endDate: end.toISOString() };
}

// Spec 216 T032: labeled quick filters — each chip states its window and scope.
const SYSTEM_QUICK_FILTERS: QuickFilter[] = [
  {
    id: 'today',
    label: 'Today',
    filters: { ...getTodayRange() },
  },
  {
    id: 'my-changes-7d',
    label: 'My Changes (7d)',
    filters: {
      ...getDefaultDateRange(),
      userEmail: '__CURRENT_USER__', // Replaced at runtime
    },
  },
  {
    id: 'recent-deletes',
    label: 'Deletes (7d)',
    filters: {
      ...getDefaultDateRange(),
      action: 'delete' as const,
    },
  },
  {
    id: 'backup-activity',
    label: 'Backup Activity (7d)',
    filters: {
      ...getDefaultDateRange(),
      // Partial entity-type match catches backup_plan / backup_schedule / backup_run.
      entityType: 'backup',
    },
  },
  {
    id: 'config-changes',
    label: 'Setting Changes (7d)',
    filters: {
      ...getDefaultDateRange(),
      // Partial entity-type match catches backup_setting / global_setting / configuration.
      entityType: 'setting',
    },
  },
];

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
];

// ============================================================================
// Detail field row (token-styled, replaces the MUI Drawer DetailRow)
// ============================================================================

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-[var(--color-text-secondary)]">{label}</div>
      <div className="mt-0.5 break-all text-sm text-[var(--color-text-primary)]">{value}</div>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export default function SystemAuditLogPage() {
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  // Filter state (applied = sent to API)
  const [appliedFilters, setAppliedFilters] = useState<SystemAuditLogFilters>({
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
  });

  // Filter form (draft) state — mirrors the old AuditFilters local state
  const [startDate, setStartDate] = useState(toDateInputValue(defaultRange.startDate));
  const [endDate, setEndDate] = useState(toDateInputValue(defaultRange.endDate));
  const [userEmail, setUserEmail] = useState('');
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [search, setSearch] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Detail modal
  const [selectedEntry, setSelectedEntry] = useState<SystemAuditLogEntry | null>(null);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [truncationDialog, setTruncationDialog] = useState<{
    open: boolean;
    total: number;
  }>({ open: false, total: 0 });

  // Quick filter active state
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);

  // Query
  const { data, isLoading, refetch } = useSystemAuditLog(appliedFilters, { page, pageSize });
  const { data: entityTypes } = useEntityTypes();
  const { data: actions } = useActions();
  const { user } = useAuth();

  // Columns — widths sum to ~100%
  const columns: DataTableColumn<SystemAuditLogEntry>[] = useMemo(
    () => [
      {
        key: 'createdAt',
        header: 'Timestamp',
        width: '17%',
        render: (row) => {
          const date = new Date(row.createdAt);
          return (
            <span title={date.toISOString()} className="text-[var(--color-text-secondary)]">
              {formatEastern(date)}
            </span>
          );
        },
      },
      {
        key: 'userEmail',
        header: 'User',
        width: '20%',
        render: (row) => row.userEmail || <span className="text-[var(--color-text-secondary)]">—</span>,
      },
      {
        key: 'action',
        header: 'Action',
        width: '11%',
        render: (row) => <ActionBadge action={row.action} />,
      },
      {
        key: 'entityType',
        header: 'Entity Type',
        width: '15%',
        render: (row) => humanizeAuditLabel(row.entityType),
      },
      {
        key: 'entityName',
        header: 'Entity Name',
        width: '27%',
        render: (row) => row.entityName || row.entityId,
      },
      {
        key: 'details',
        header: '',
        width: '10%',
        align: 'right',
        render: (row) => (
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Eye className="h-4 w-4" />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedEntry(row);
            }}
          >
            Details
          </Button>
        ),
      },
    ],
    []
  );

  // Build + apply the query from the current draft, allowing inline overrides so a dropdown can
  // apply with its NEW value immediately (setState is async — we can't read it back synchronously).
  const applyFilters = useCallback(
    (overrides: Partial<{ action: string; entityType: string; userEmail: string; search: string }> = {}) => {
      const a = overrides.action ?? action;
      const et = overrides.entityType ?? entityType;
      const ue = overrides.userEmail ?? userEmail;
      const se = overrides.search ?? search;
      const filters: SystemAuditLogFilters = {
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
        ...(ue ? { userEmail: ue } : {}),
        ...(a ? { action: a } : {}),
        ...(et ? { entityType: et } : {}),
        ...(se ? { search: se } : {}),
      };
      setAppliedFilters(filters);
      setPage(1);
      setActiveQuickFilter(null);
    },
    [startDate, endDate, userEmail, action, entityType, search]
  );
  const handleApplyFilters = useCallback(() => applyFilters(), [applyFilters]);

  const handleClearFilters = useCallback(() => {
    const range = getDefaultDateRange();
    setStartDate(toDateInputValue(range.startDate));
    setEndDate(toDateInputValue(range.endDate));
    setUserEmail('');
    setAction('');
    setEntityType('');
    setSearch('');
    setAppliedFilters({ startDate: range.startDate, endDate: range.endDate });
    setPage(1);
    setActiveQuickFilter(null);
  }, []);

  const handleQuickFilter = useCallback(
    (filter: QuickFilter) => {
      const raw = { ...(filter.filters as Partial<SystemAuditLogFilters>) };
      // "My Changes" stores a sentinel — resolve it to the signed-in user's email at click time.
      if (raw.userEmail === '__CURRENT_USER__') raw.userEmail = user?.email ?? '';
      const newFilters: SystemAuditLogFilters = {
        startDate: defaultRange.startDate,
        endDate: defaultRange.endDate,
        ...raw,
      };
      setAppliedFilters(newFilters);
      // Mirror into the draft form so the inputs reflect the active quick filter.
      setUserEmail(raw.userEmail ?? '');
      setAction((raw.action as string) ?? '');
      setEntityType((raw.entityType as string) ?? '');
      setPage(1);
      setActiveQuickFilter(filter.id);
    },
    [defaultRange, user]
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const result = await exportSystemAuditCsv(appliedFilters);
      if (result.blob.size === 0) {
        return; // 204 No Content
      }

      // Trigger download
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `system-audit-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (result.truncated) {
        setTruncationDialog({ open: true, total: result.total });
      }
    } catch {
      // Error handled by apiClient interceptor
    } finally {
      setExporting(false);
    }
  }, [appliedFilters]);

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasData = rows.length > 0;

  const activeFilterCount =
    (userEmail ? 1 : 0) + (action ? 1 : 0) + (entityType ? 1 : 0) + (search ? 1 : 0);

  const emptyMessage =
    appliedFilters.action ||
    appliedFilters.entityType ||
    appliedFilters.userEmail ||
    appliedFilters.search
      ? 'No audit entries match the selected filters'
      : 'No audit entries recorded yet';

  const detailTs = selectedEntry ? new Date(selectedEntry.createdAt) : null;

  return (
    <PageLayout
      helpId="auto.system-audit-log"
      title="System Audit Log"
      description="Configuration and entity changes across the platform"
      icon={<ListFilter className="h-6 w-6" />}
      actions={
        <>
          <Button
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={handleExport}
            disabled={!hasData || exporting}
            isLoading={exporting}
          >
            Export to CSV
          </Button>
          <Button
            variant="outline"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        </>
      }
    >
      {/* Quick filters (Spec 216 T032: labeled row) */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1 text-sm font-medium text-[var(--color-text-secondary)]">
          <ListFilter className="h-4 w-4" /> Quick filters:
        </span>
        {SYSTEM_QUICK_FILTERS.map((filter) => {
          const active = activeQuickFilter === filter.id;
          return (
            <Button
              key={filter.id}
              size="sm"
              variant={active ? 'primary' : 'outline'}
              onClick={() => handleQuickFilter(filter)}
            >
              {filter.label}
            </Button>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
          <div className="md:col-span-3">
            <Input
              isFullWidth
              label="Start Date"
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="md:col-span-3">
            <Input
              isFullWidth
              label="End Date"
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="md:col-span-3">
            <Input
              isFullWidth
              label="User Email"
              placeholder="Filter by email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
            />
          </div>
          <div className="md:col-span-3">
            <Select
              isFullWidth
              label="Action"
              value={action}
              onChange={(e) => { setAction(e.target.value); applyFilters({ action: e.target.value }); }}
              options={[
                { value: '', label: 'All Actions' },
                // Dynamic: every action actually recorded (backup/restore/plan/schedule/settings +
                // future); falls back to the basic CRUD set if the log is still empty.
                ...(actions && actions.length
                  ? actions.map((a) => ({ value: a, label: humanizeAuditLabel(a) }))
                  : ACTION_OPTIONS.slice(1)),
              ]}
            />
          </div>
          <div className="md:col-span-4">
            <Select
              isFullWidth
              label="Entity Type"
              value={entityType}
              onChange={(e) => { setEntityType(e.target.value); applyFilters({ entityType: e.target.value }); }}
              options={[
                { value: '', label: 'All Entity Types' },
                ...(entityTypes ?? []).map((t) => ({ value: t, label: humanizeAuditLabel(t) })),
              ]}
            />
          </div>
          <div className="md:col-span-4">
            <Input
              isFullWidth
              label="Search"
              placeholder="Entity name or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
            />
          </div>
          <div className="flex gap-2 md:col-span-4">
            <Button
              variant="primary"
              leftIcon={<Filter className="h-4 w-4" />}
              onClick={handleApplyFilters}
            >
              Apply Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
            <Button
              variant="ghost"
              leftIcon={<XIcon className="h-4 w-4" />}
              onClick={handleClearFilters}
            >
              Clear
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        {isLoading ? (
          <EmptyState title="Loading audit entries…" />
        ) : !hasData ? (
          <EmptyState title={emptyMessage} icon={<ListFilter className="h-10 w-10" />} />
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              onRowClick={(row) => setSelectedEntry(row)}
              emptyMessage={emptyMessage}
              pageSize={pageSize}
              /* This page owns pagination (server-side, with its own page
                 controls), so the table must not render a second one. The blueprint's
                 DataTable rules name this case: "Without it you get two paginators
                 stacked." */
              showPagination={false}
              minWidth="52rem"
            />
            {/* Server-side pager (the API already returns exactly one page). */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
              <span>
                {total.toLocaleString()} total · page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Select
                  value={String(pageSize)}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setPage(1);
                  }}
                  options={[
                    { value: '25', label: '25 / page' },
                    { value: '50', label: '50 / page' },
                    { value: '100', label: '100 / page' },
                  ]}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Detail modal */}
      <Modal
        isOpen={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
        title="Audit Log Entry"
        size="md"
      >
        {selectedEntry && (
          <div className="space-y-4">
            <DetailRow
              label="Timestamp"
              value={
                <span title={detailTs?.toISOString()}>{formatEastern(detailTs)}</span>
              }
            />
            <DetailRow label="User Email" value={selectedEntry.userEmail} />
            <DetailRow label="Action" value={<ActionBadge action={selectedEntry.action} />} />
            <DetailRow label="Entity Type" value={humanizeAuditLabel(selectedEntry.entityType)} />
            <DetailRow label="Entity ID" value={selectedEntry.entityId} />
            <DetailRow label="Entity Name" value={selectedEntry.entityName} />
            <DetailRow label="IP Address" value={selectedEntry.ipAddress} />
            <DetailRow label="User Agent" value={selectedEntry.userAgent} />
            <DetailRow label="Client Account ID" value={selectedEntry.clientAccountId} />
            {selectedEntry.changes && selectedEntry.changes.length > 0 && (
              <div>
                <div className="mb-1 text-xs uppercase tracking-wide text-[var(--color-text-secondary)]">
                  Changes
                </div>
                <ChangesDiff changes={selectedEntry.changes} />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Export truncation warning */}
      <Modal
        isOpen={truncationDialog.open}
        onClose={() => setTruncationDialog({ open: false, total: 0 })}
        title="Export Limited"
        size="sm"
        footer={
          <Button onClick={() => setTruncationDialog({ open: false, total: 0 })}>OK</Button>
        }
      >
        <Alert variant="warning">
          Export limited to 10,000 records. {truncationDialog.total.toLocaleString()} total records
          matched. Apply narrower filters for complete data.
        </Alert>
      </Modal>
    </PageLayout>
  );
}
